import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { DownOutlined } from '@ant-design/icons';
import Markdown from '@/components/ui/Markdown';

const EASE = [0.25, 0.1, 0.25, 1];
const DURATION = 0.42;

/**
 * Phase description: collapsed to 3 lines with ellipsis; smooth height expand/collapse when toggling read more.
 */
export default function PhaseDescriptionCollapsible({ source, resetKey, attachmentItems, onLinkClick, alwaysExpanded }) {
    const trimmed = typeof source === 'string' ? source.trim() : '';
    const [measurePass, setMeasurePass] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [isCollapsing, setIsCollapsing] = useState(false);
    const [hasLongContent, setHasLongContent] = useState(false);
    const [collapsedH, setCollapsedH] = useState(null);
    const [fullH, setFullH] = useState(null);
    const [hover, setHover] = useState(false);
    const contentRef = useRef(null);
    /** Avoid resetting measurement on mount: useLayoutEffect runs first, then useEffect would wipe hasLongContent. */
    const prevPhaseSourceRef = useRef(null);
    const reduceMotion = useReducedMotion();

    const lineClampActive = !expanded && !isCollapsing;

    const transition = useMemo(
        () => (reduceMotion ? { duration: 0 } : { duration: DURATION, ease: EASE }),
        [reduceMotion]
    );

    useEffect(() => {
        const prev = prevPhaseSourceRef.current;
        const next = { resetKey, trimmed };
        if (prev && prev.resetKey === next.resetKey && prev.trimmed === next.trimmed) {
            return;
        }
        prevPhaseSourceRef.current = next;
        if (!prev) {
            return;
        }
        setMeasurePass(true);
        setExpanded(false);
        setIsCollapsing(false);
        setHasLongContent(false);
        setCollapsedH(null);
        setFullH(null);
    }, [resetKey, trimmed]);

    useLayoutEffect(() => {
        const el = contentRef.current;
        if (!el || !trimmed) return;

        if (measurePass) {
            const long = el.scrollHeight > el.clientHeight + 1;
            setHasLongContent(long);
            if (long) {
                setCollapsedH(el.clientHeight);
                setFullH(el.scrollHeight);
            }
            setMeasurePass(false);
            return;
        }

        if (!hasLongContent) return;

        if (lineClampActive) {
            setCollapsedH(el.clientHeight);
            setFullH(el.scrollHeight);
        } else {
            setFullH(el.scrollHeight);
        }
    }, [trimmed, measurePass, hasLongContent, lineClampActive, expanded, isCollapsing]);

    if (!trimmed) return null;

    const secondary = 'var(--ant-color-text-secondary, rgba(0, 0, 0, 0.45))';
    const primary = 'var(--ant-color-primary, #7c3aed)';

    if (measurePass) {
        return (
            <div style={{ width: '100%' }}>
                <Markdown
                    source={trimmed}
                    variant="compact"
                    lineClamp={3}
                    contentRef={contentRef}
                    linkPrimary
                    richInlineAnchors
                    attachmentItems={attachmentItems}
                    onLinkClick={onLinkClick}
                />
            </div>
        );
    }

    if (!hasLongContent || alwaysExpanded) {
        return (
            <div style={{ width: '100%' }}>
                <Markdown
                    source={trimmed}
                    variant="compact"
                    linkPrimary
                    richInlineAnchors
                    attachmentItems={attachmentItems}
                    onLinkClick={onLinkClick}
                />
            </div>
        );
    }

    const targetHeight = expanded ? fullH : collapsedH;
    const canAnimate = collapsedH != null && fullH != null;

    const handleToggle = () => {
        if (expanded) {
            setIsCollapsing(true);
            setExpanded(false);
        } else {
            setExpanded(true);
        }
    };

    const handleAnimationComplete = () => {
        setIsCollapsing((s) => (s ? false : s));
    };

    return (
        <div style={{ width: '100%' }}>
            <motion.div
                initial={false}
                animate={canAnimate && targetHeight != null ? { height: targetHeight } : undefined}
                transition={transition}
                onAnimationComplete={handleAnimationComplete}
                style={{
                    overflow: 'hidden',
                    width: '100%',
                }}
            >
                <Markdown
                    source={trimmed}
                    variant="compact"
                    lineClamp={lineClampActive ? 3 : undefined}
                    contentRef={contentRef}
                    linkPrimary
                    richInlineAnchors
                    attachmentItems={attachmentItems}
                    onLinkClick={onLinkClick}
                />
            </motion.div>
            {hasLongContent && (
                <button
                    type="button"
                    onClick={handleToggle}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        marginTop: 10,
                        padding: '2px 0',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 500,
                        letterSpacing: '0.01em',
                        fontFamily: 'var(--font-family-geist), system-ui, sans-serif',
                        color: hover ? primary : secondary,
                        transition: 'color 0.2s ease',
                    }}
                >
                    <span
                        style={{
                            borderBottom: `1px solid ${hover ? primary : 'transparent'}`,
                            paddingBottom: 1,
                            transition: 'border-color 0.2s ease',
                        }}
                    >
                        {expanded ? 'Show less' : 'Read more'}
                    </span>
                    <span
                        style={{
                            display: 'inline-flex',
                            lineHeight: 0,
                            transition: reduceMotion ? 'none' : 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            color: 'inherit',
                        }}
                    >
                        <DownOutlined style={{ fontSize: 11 }} />
                    </span>
                </button>
            )}
        </div>
    );
}
